import { Camera, Eye, ImageIcon, Lock, Star } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useEffect } from "react"
import { useNavigate } from "react-router"
import { match } from "ts-pattern"
import { mediaUrl } from "../lib/api.js"
import { isTelegramMiniApp } from "../lib/telegram.js"
import { useStore, useT } from "../stores/StoreProvider.js"

interface GalleryCardProps {
  gallery: {
    id: string
    slug: string
    title: string
    coverMediaId: string | null
    mediaCount: number
    visibility: string
    pricingType: string
  }
}

const visibilityConfig = {
  public: { label: "Public", icon: Eye },
  unlisted: { label: "Unlisted", icon: Eye },
  private: { label: "Private", icon: Lock },
  paid: { label: "Paid", icon: Star },
} as const

interface GalleryCardInnerProps extends GalleryCardProps {
  index: number
}

const GalleryCard = observer(function GalleryCard({ gallery, index }: GalleryCardInnerProps) {
  const vis = visibilityConfig[gallery.visibility as keyof typeof visibilityConfig]
  const VisIcon = vis?.icon ?? Eye
  const navigate = useNavigate()
  const t = useT()

  return (
    <button
      type="button"
      onClick={() => navigate(`/g/${gallery.slug}`)}
      className="card-enter block w-full text-left rounded-2xl overflow-hidden bg-tg-surface btn-press"
      style={{
        animationDelay: `${index * 60}ms`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      <div className="aspect-[4/3] bg-tg-secondary overflow-hidden relative">
        {gallery.coverMediaId ? (
          <>
            <img
              src={mediaUrl(gallery.coverMediaId)}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: "linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.25) 100%)",
              }}
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-10 h-10 text-tg-hint/40" />
          </div>
        )}
        {vis && gallery.visibility !== "public" && (
          <span className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-black/50 px-2 py-0.5 text-[11px] text-white backdrop-blur-md">
            <VisIcon className="w-3 h-3" />
            {vis.label}
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="font-semibold text-[15px] text-tg-text truncate">{gallery.title}</div>
        <div className="text-tg-hint text-[13px] mt-0.5 flex items-center gap-1">
          <ImageIcon className="w-3 h-3" />
          {gallery.mediaCount} {t("home.photos")}
        </div>
      </div>
    </button>
  )
})

function GalleryCardSkeleton() {
  return (
    <div className="rounded-xl overflow-hidden bg-tg-surface">
      <div className="aspect-[4/3] skeleton" />
      <div className="p-3 space-y-2">
        <div className="skeleton h-4 w-3/4 rounded" />
        <div className="skeleton h-3 w-1/3 rounded" />
      </div>
    </div>
  )
}

function SkeletonGrid() {
  return (
    <div className="p-4">
      <div className="skeleton h-6 w-36 rounded mb-4" />
      <div className="grid grid-cols-2 gap-3">
        <GalleryCardSkeleton />
        <GalleryCardSkeleton />
        <GalleryCardSkeleton />
        <GalleryCardSkeleton />
      </div>
    </div>
  )
}

const WelcomeMessage = observer(function WelcomeMessage() {
  const inTelegram = isTelegramMiniApp()
  const t = useT()

  return (
    <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-tg-accent/10 flex items-center justify-center mb-4">
        <Camera className="w-8 h-8 text-tg-accent" />
      </div>
      <p className="text-tg-text font-medium mb-1">{t("home.welcome")}</p>
      <p className="text-tg-hint text-sm max-w-[260px]">
        {inTelegram
          ? t("home.send_photos_hint")
          : t("home.open_in_telegram")}
      </p>
    </div>
  )
})

export const GalleryList = observer(function GalleryList() {
  const { galleryStore, authStore } = useStore()
  const t = useT()

  useEffect(() => {
    if (authStore.isAuthenticated) {
      void galleryStore.fetchMyGalleries()
    }
  }, [authStore.isAuthenticated, galleryStore])

  // While auth is resolving, show the skeleton to prevent unauthenticated flash
  const authResolving =
    authStore.state.status === "idle" || authStore.state.status === "authenticating"

  if (authResolving) {
    return <SkeletonGrid />
  }

  // Auth settled but failed -- show welcome instead of confusing "Sign in"
  if (authStore.state.status === "error") {
    return <WelcomeMessage />
  }

  // Authenticated -- render based on gallery list state
  return match(galleryStore.listState)
    .with({ status: "idle" }, () => <SkeletonGrid />)
    .with({ status: "loading" }, () => <SkeletonGrid />)
    .with({ status: "error" }, ({ message }) => (
      <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
        <p className="text-tg-destructive text-sm">{message}</p>
      </div>
    ))
    .with({ status: "loaded" }, ({ galleries }) =>
      galleries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-20 h-20 rounded-full bg-tg-accent/10 flex items-center justify-center mb-4">
            <ImageIcon className="w-10 h-10 text-tg-accent/60" />
          </div>
          <p className="text-tg-text font-medium mb-1">{t("home.no_galleries_yet")}</p>
          <p className="text-tg-hint text-sm">{t("home.send_photos_hint")}</p>
        </div>
      ) : (
        <div className="p-4 page-enter">
          <h2 className="text-lg font-semibold mb-3 text-tg-text">{t("home.my_galleries")}</h2>
          <div className="grid grid-cols-2 gap-3">
            {galleries.map((g, i) => (
              <GalleryCard key={g.id} gallery={g} index={i} />
            ))}
          </div>
        </div>
      )
    )
    .exhaustive()
})
